import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { useProjects, useCreateProject } from '../../hooks/useProjects';
import { useUpdateLead } from '../../hooks/useLeads';
import { useEscapeHandler } from '../../hooks/useEscapeHandler';
import { toErrorMessage } from '../../utils/toErrorMessage';
import type { Lead, LeadProject } from '../../services/api/leads';
import type { ProjectListItem } from '../../services/api/projects';

interface SetupProjectsModalProps {
  lead: Lead;
  isOpen: boolean;
  onClose: () => void;
}

interface SectionValue {
  mode: 'link' | 'create';
  projectId: number | null;
  createText: string;
  parentId: number | null;
}

function isProjectActive(p: ProjectListItem): boolean {
  return p.state !== 'cancelled' && p.state !== 'archived';
}

/**
 * Two-section (Billable / Non-billable) combobox-with-inline-create field.
 * Mirrors the ProjectFormPage customer dropdown pattern (input + filtered
 * dropdown of existing options + "Create ..." option when no exact match).
 */
function ProjectPickerSection({
  sectionLabel,
  projects,
  initialLinkedProject,
  customerName,
  allActiveProjects,
  value,
  onChange,
}: {
  sectionLabel: string;
  projects: ProjectListItem[];
  initialLinkedProject: LeadProject | null;
  customerName: string;
  allActiveProjects: ProjectListItem[];
  value: SectionValue;
  onChange: (next: SectionValue) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const linkedName =
    projects.find((p) => p.id === value.projectId)?.fullName ??
    projects.find((p) => p.id === value.projectId)?.name ??
    (initialLinkedProject && value.projectId === initialLinkedProject.id
      ? initialLinkedProject.name
      : null) ??
    '';

  const displayValue = isOpen
    ? filter
    : value.mode === 'link'
      ? linkedName
      : value.createText
        ? `Create "${value.createText}"`
        : '';

  const trimmedFilter = filter.trim();
  const filtered = projects.filter((p) =>
    (p.fullName || p.name || '').toLowerCase().includes(filter.toLowerCase())
  );
  const isNew =
    trimmedFilter !== '' &&
    !projects.some((p) => (p.fullName || p.name || '').toLowerCase() === trimmedFilter.toLowerCase());

  return (
    <div className="space-y-2">
      <label htmlFor={`setup-projects-input-${sectionLabel}`} className="block text-sm font-medium text-gray-700">
        {sectionLabel}
      </label>
      {value.mode === 'create' && (
        <div>
          <label
            htmlFor={`setup-projects-parent-${sectionLabel}`}
            className="block text-xs font-medium text-gray-500 mb-1"
          >
            Parent project (optional)
          </label>
          <select
            id={`setup-projects-parent-${sectionLabel}`}
            value={value.parentId ?? ''}
            onChange={(e) =>
              onChange({ ...value, parentId: e.target.value ? Number(e.target.value) : null })
            }
            className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">No parent (top-level project)</option>
            {allActiveProjects
              .slice()
              .sort((a, b) => (a.fullName || a.name).localeCompare(b.fullName || b.name))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName || p.name}
                </option>
              ))}
          </select>
        </div>
      )}

      <div
        className={`relative ${value.mode === 'create' ? 'ml-3 pl-3 border-l-2 border-gray-100' : ''}`}
        ref={dropdownRef}
      >
        <input
          id={`setup-projects-input-${sectionLabel}`}
          type="text"
          value={displayValue}
          onChange={(e) => {
            setFilter(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            setFilter('');
            setIsOpen(true);
          }}
          placeholder="Select or create a project..."
          className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? `Close ${sectionLabel} dropdown` : `Open ${sectionLabel} dropdown`}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
        >
          <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-auto">
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onChange({ ...value, mode: 'link', projectId: p.id });
                  setFilter('');
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                  value.mode === 'link' && value.projectId === p.id
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700'
                }`}
              >
                {p.fullName || p.name}
              </button>
            ))}
            {isNew && (
              <button
                type="button"
                onClick={() => {
                  onChange({ ...value, mode: 'create', projectId: null, createText: trimmedFilter });
                  setFilter('');
                  setIsOpen(false);
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 text-green-700 border-t border-gray-100 flex items-center gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                Create &ldquo;{trimmedFilter}&rdquo;
              </button>
            )}
            {filtered.length === 0 && !isNew && (
              <p className="px-3 py-2 text-sm text-gray-400">No projects found</p>
            )}
          </div>
        )}
      </div>

      {value.mode === 'create' && (
        <p className="ml-3 pl-3 border-l-2 border-transparent text-xs text-gray-400">
          Customer: <span className="text-gray-600">{customerName}</span>
        </p>
      )}
    </div>
  );
}

const EMPTY_SECTION: SectionValue = { mode: 'create', projectId: null, createText: '', parentId: null };

export default function SetupProjectsModal({ lead, isOpen, onClose }: SetupProjectsModalProps) {
  const { data: allProjects } = useProjects({ includeArchived: false });
  const createProjectMutation = useCreateProject();
  const updateLeadMutation = useUpdateLead();
  const navigate = useNavigate();

  const [billable, setBillable] = useState<SectionValue>(EMPTY_SECTION);
  const [nonBillable, setNonBillable] = useState<SectionValue>(EMPTY_SECTION);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEscapeHandler(isOpen, onClose);

  // Seed sensible defaults each time the modal opens for a (possibly
  // different) lead. Also re-seeds when the lead's linked project ids
  // change while open: the ?setupProjects deep-link reopens the modal
  // before the board query's background refetch lands, so the fresh
  // convertedProject/nonBillableProject would otherwise be ignored and the
  // section would stay in create mode for an already-created project. A
  // plain projects refetch (ids unchanged) still never clobbers user edits.
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setIsSaving(false);

    if (lead.convertedProject) {
      setBillable({ mode: 'link', projectId: lead.convertedProject.id, createText: lead.name, parentId: null });
    } else {
      setBillable({ mode: 'create', projectId: null, createText: lead.name, parentId: null });
    }

    if (lead.nonBillableProject) {
      setNonBillable({ mode: 'link', projectId: lead.nonBillableProject.id, createText: '', parentId: null });
    } else {
      const activeProjects = allProjects ?? [];
      const customerNonBillable = activeProjects.filter(
        (p) => isProjectActive(p) && p.billable === false && p.customer?.id === lead.customer.id
      );
      if (customerNonBillable.length === 1) {
        setNonBillable({ mode: 'link', projectId: customerNonBillable[0]!.id, createText: '', parentId: null });
      } else {
        setNonBillable({
          mode: 'create',
          projectId: null,
          createText: `${lead.customer.name ?? ''} — non-billable`.trim(),
          parentId: null,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lead.id, lead.convertedProject?.id, lead.nonBillableProject?.id]);

  if (!isOpen) return null;

  const activeProjects = (allProjects ?? []).filter(isProjectActive);
  const billableProjects = activeProjects.filter((p) => p.billable === true);
  const nonBillableProjects = activeProjects.filter((p) => p.billable === false);

  const isResolved = (v: SectionValue) =>
    v.mode === 'link' ? v.projectId !== null : v.createText.trim() !== '';

  const canSave = isResolved(billable) && isResolved(nonBillable) && !isSaving;

  const handleSave = async () => {
    setError(null);
    setIsSaving(true);
    try {
      let billableId = billable.projectId;
      if (billable.mode === 'create') {
        const created = await createProjectMutation.mutateAsync({
          name: billable.createText.trim(),
          customerId: lead.customer.id,
          parentId: billable.parentId ?? undefined,
          billable: true,
        });
        billableId = created.id;
      }

      let nonBillableId = nonBillable.projectId;
      if (nonBillable.mode === 'create') {
        const created = await createProjectMutation.mutateAsync({
          name: nonBillable.createText.trim(),
          customerId: lead.customer.id,
          parentId: nonBillable.parentId ?? undefined,
          billable: false,
        });
        nonBillableId = created.id;
      }

      await updateLeadMutation.mutateAsync({
        id: lead.id,
        input: { convertedProjectId: billableId, nonBillableProjectId: nonBillableId },
      });

      setIsSaving(false);
      onClose();
    } catch (err) {
      setIsSaving(false);
      setError(toErrorMessage(err, "Couldn't set up projects"));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-full max-w-lg max-h-[90vh] flex flex-col rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between mb-4 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Projects for {lead.name}</h2>
        </div>

        <div className="space-y-5 overflow-y-auto pr-1 -mr-1 flex-1">
          <div className="space-y-1">
            <ProjectPickerSection
              sectionLabel="Billable"
              projects={billableProjects}
              initialLinkedProject={lead.convertedProject}
              customerName={lead.customer.name ?? '—'}
              allActiveProjects={activeProjects}
              value={billable}
              onChange={setBillable}
            />
            {billable.mode === 'create' && (
              <button
                type="button"
                onClick={() => {
                  navigate(`/projects/new?fromLead=${lead.id}`);
                  onClose();
                }}
                className="ml-3 pl-3 text-xs text-gray-400 underline hover:text-gray-600"
              >
                Create the billable project in the full form instead
              </button>
            )}
          </div>
          <div className="space-y-1">
            <ProjectPickerSection
              sectionLabel="Non-billable"
              projects={nonBillableProjects}
              initialLinkedProject={lead.nonBillableProject}
              customerName={lead.customer.name ?? '—'}
              allActiveProjects={activeProjects}
              value={nonBillable}
              onChange={setNonBillable}
            />
            {nonBillable.mode === 'create' && (
              <button
                type="button"
                onClick={() => {
                  navigate(`/projects/new?fromLead=${lead.id}&leadRole=nonBillable`);
                  onClose();
                }}
                className="ml-3 pl-3 text-xs text-gray-400 underline hover:text-gray-600"
              >
                Create the non-billable project in the full form instead
              </button>
            )}
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex items-center justify-end pt-4 mt-2 border-t border-gray-100 flex-shrink-0">
          <div className="flex gap-3">
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" isLoading={isSaving} disabled={!canSave} onClick={handleSave}>
              Save both
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
